import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HikeCard from '../components/HikeCard';
import { API_URL } from '../config';
import Spinner from '../components/Spinner';
import Footer from '../components/Footer';

export default function ArchivedHikesPage() {
  const navigate = useNavigate();
  const { token, hasPermission } = useAuth();
  const [hikes, setHikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchArchivedHikes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/hikes/archived`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Impossibile caricare le gite archiviate');
      }

      const data = await response.json();
      setHikes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Only organizers and admins can access this page
    if (!hasPermission('hike:view_hidden:any')) {
      navigate('/');
      return;
    }
    fetchArchivedHikes();
  }, [token, hasPermission, navigate, fetchArchivedHikes]);

  // Filter by search query
  const filteredHikes = searchQuery.trim()
    ? hikes.filter(hike =>
        hike.titolo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hike.luogo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hike.organizers?.some(org =>
          org.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : hikes;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="md" color="blue" />
          <div className="text-sm text-gray-600">Caricamento gite archiviate...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <h1 className="text-lg font-semibold text-gray-800">
            Gite Archiviate
          </h1>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            &larr; Torna alle gite attive
          </button>
        </div>

        {/* Search */}
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per titolo, luogo o organizzatore..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-2 text-xs text-gray-500">
            {filteredHikes.length} {filteredHikes.length === 1 ? 'gita archiviata' : 'gite archiviate'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-4">
            {error}
          </div>
        )}

        {/* Hike Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredHikes.map(hike => (
            <HikeCard
              key={hike.id}
              hike={hike}
            />
          ))}
        </div>

        {filteredHikes.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              {searchQuery ? 'Nessuna gita archiviata trovata.' : 'Nessuna gita archiviata.'}
            </p>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
